import { type Placement } from '@floating-ui/react';
import { faCalendar, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, type ReactNode } from 'react';
import DatePicker from 'react-datepicker';
import { tv } from 'tailwind-variants';

import { dateFromDateStringWithAppendedTime } from '@/utils/dates';

import { cn } from '../lib/utils';

import 'react-datepicker/dist/react-datepicker.css';

const datetimeVariants = tv({
  slots: {
    wrapper: [
      'flex items-stretch rounded-lg border border-uniform-gray-300 bg-white',
      'focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent',
      'dark:border-uniform-gray-600 dark:bg-uniform-gray-900 transition-shadow'
    ],
    input: [
      'min-w-0 flex-1 bg-transparent py-2 text-sm',
      'text-uniform-gray-900 placeholder:text-uniform-gray-400',
      '[outline:none]',
      'dark:text-uniform-gray-100 dark:placeholder:text-uniform-gray-500'
    ],
    icon: 'flex shrink-0 items-center px-3 text-uniform-gray-400',
    clearBtn: [
      'flex shrink-0 items-center px-2',
      'text-uniform-gray-400 hover:text-uniform-gray-600',
      'dark:text-uniform-gray-500 dark:hover:text-uniform-gray-300'
    ],
    label: 'block text-sm font-medium text-uniform-gray-700 dark:text-uniform-gray-300',
    helpText: 'text-sm text-uniform-gray-500 dark:text-uniform-gray-400',
    errorText: 'text-sm text-red-500 dark:text-red-400'
  },
  variants: {
    disabled: {
      true: { wrapper: 'opacity-50 cursor-not-allowed', input: 'cursor-not-allowed' }
    },
    hasError: {
      true: {
        wrapper:
          'border-red-400 dark:border-red-500 focus-within:ring-red-400 dark:focus-within:ring-red-500'
      }
    }
  }
});

interface CustomInputProps {
  value?: string;
  onClick?: () => void;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  onBlur?: () => void;
  autoFocus?: boolean;
  // injected by DatetimePicker (not from react-datepicker)
  isClearable?: boolean;
  onClear?: () => void;
  hasError?: boolean;
  wrapperClassName?: string;
}

const CustomDateInput = forwardRef<HTMLInputElement, CustomInputProps>(
  (
    {
      value,
      onClick,
      onChange,
      id,
      placeholder,
      disabled,
      onBlur,
      autoFocus,
      isClearable,
      onClear,
      hasError,
      wrapperClassName
    },
    ref
  ) => {
    const slots = datetimeVariants({ disabled, hasError });
    return (
      <div className={cn(slots.wrapper(), wrapperClassName)}>
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          className={slots.icon()}
          tabIndex={-1}>
          <FontAwesomeIcon icon={faCalendar} className="h-3.5 w-3.5" />
        </button>
        <input
          ref={ref}
          id={id}
          value={value ?? ''}
          onChange={onChange}
          onClick={onClick}
          placeholder={placeholder}
          disabled={disabled}
          onBlur={onBlur}
          autoFocus={autoFocus}
          readOnly={!onChange}
          className={slots.input()}
        />
        {isClearable && !!value && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClear?.();
            }}
            disabled={disabled}
            aria-label="Close"
            className={slots.clearBtn()}
            tabIndex={-1}>
            <FontAwesomeIcon icon={faTimesCircle} className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  }
);
CustomDateInput.displayName = 'CustomDateInput';

export interface DatetimePickerProps {
  autoFocus?: boolean;
  dateFormat?: string;
  disabled?: boolean;
  error?: string;
  filterDate?: (date: Date) => boolean;
  helpText?: string;
  isClearable?: boolean;
  label: string | ReactNode;
  minDate?: Date;
  name: string;
  onBlur?: () => void;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  placement?: Placement;
  preventOpenOnFocus?: boolean;
  showTimeSelect?: boolean;
  value: string | null | undefined;
  withPortal?: boolean;
}

function DatetimePicker({
  autoFocus = false,
  dateFormat,
  disabled = false,
  error,
  filterDate,
  helpText,
  isClearable = true,
  label,
  minDate,
  name,
  onBlur,
  onChange,
  placeholder,
  placement = 'bottom-start',
  preventOpenOnFocus = false,
  showTimeSelect = true,
  value,
  withPortal = false
}: DatetimePickerProps) {
  const resolvedFormat = dateFormat ?? (showTimeSelect ? 'MMMM d, yyyy h:mm aa' : 'MMMM d, yyyy');
  const resolvedPlaceholder = placeholder ?? (showTimeSelect ? '--/--/---- --:--' : '--/--/----');
  const dateValue = value ? dateFromDateStringWithAppendedTime(value) : null;
  const labelId = `datepicker-${name}`;

  const customInput = (
    <CustomDateInput isClearable={isClearable} onClear={() => onChange(null)} hasError={!!error} />
  );

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={labelId} className={datetimeVariants().label()}>
        {label}
      </label>
      {helpText && <p className={datetimeVariants().helpText()}>{helpText}</p>}
      <DatePicker
        autoFocus={autoFocus}
        customInput={customInput}
        dateFormat={resolvedFormat}
        disabled={disabled}
        filterDate={filterDate}
        id={labelId}
        minDate={minDate}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        placeholderText={resolvedPlaceholder}
        popperClassName="!z-[9999]"
        popperPlacement={placement}
        popperProps={{ strategy: 'fixed' }}
        preventOpenOnFocus={preventOpenOnFocus}
        scrollableYearDropdown
        selected={dateValue}
        shouldCloseOnSelect
        showTimeSelect={showTimeSelect}
        showYearDropdown
        withPortal={withPortal}
        wrapperClassName="w-full"
        yearDropdownItemNumber={30}
      />
      {error && <p className={datetimeVariants({ hasError: true }).errorText()}>{error}</p>}
    </div>
  );
}

export { DatetimePicker };
